import {
  faArchive,
  faBoxArchive,
  faEdit,
  faLink,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DeleteButton from "../../Helpers/deleteButton";
import { Questionary } from "../../models/Questionary";
import QuestionaryService, {
  PagedResult,
} from "../../services/questionary.service";
import useUser from "../App/useUser";
import { ModalNewQuestionary } from "./ModalNewQuestionary";
import "./style.css";

import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Pagination,
  Row,
  Stack,
  Table,
  Spinner,
} from "react-bootstrap";
import ArchiveButton from "../../Helpers/archiveButton";
import ModalArchivedQuestionaries from "./ModalArchivedQuestionaries/ModalArchivedQuestionaries";

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Type guard para detectar si es PagedResult o Array
function isPagedResult<T>(
  response: T[] | PagedResult<T>,
): response is PagedResult<T> {
  return (response as PagedResult<T>).data !== undefined;
}

// Debounce hook para búsqueda
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================
// TYPES
// ============================================

type Props = { userId: number };
type Notification = {
  show: boolean;
  message: string;
  title: string;
  variant: "success" | "danger";
};

// ============================================
// COMPONENT
// ============================================

export const Questionaries = (props: Props) => {
  // Estados principales
  const [questionaries, setQuestionaries] = useState<Questionary[]>([]);
  const [showOnlyCompleted, setShowOnlyCompleted] = useState<boolean>(false);
  const [modalShow, setModalShow] = useState(false);
  const { user, isLoading } = useUser();
  const [notification, setNotification] = useState<Notification | null>(null);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  // Estados de búsqueda y paginación
  const [filterText, setFilterText] = useState("");
  const debouncedSearchText = useDebounce(filterText, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // Default: 50 items por página
  const [showArchivedModal, setShowArchivedModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Estados de paginación del servidor
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Función para cargar cuestionarios con paginación
  const loadQuestionaries = useCallback(async () => {
    if (!user) {
      setQuestionaries([]);
      return;
    }

    setLoading(true);
    try {
      let result: Questionary[] | PagedResult<Questionary>;

      // Llamar al servicio CON paginación
      if (user.role === "Administrador") {
        result = await QuestionaryService.getAllQuestionaries(
          showOnlyCompleted,
          currentPage,
          itemsPerPage,
          false,
          debouncedSearchText,
        );
      } else if (user.role === "Auditor") {
        result = await QuestionaryService.getQuestionariesByUserId(
          props.userId,
          showOnlyCompleted,
          currentPage,
          itemsPerPage,
          false,
          debouncedSearchText,
        );
      } else {
        result = await QuestionaryService.getQuestionariesByEmail(
          user.email || "",
          showOnlyCompleted,
          currentPage,
          itemsPerPage,
          false,
          debouncedSearchText,
        );
      }

      // Detectar tipo de respuesta usando type guard
      if (isPagedResult(result)) {
        // Es PagedResult - modo optimizado
        setQuestionaries(result.data);
        setTotalRecords(result.totalRecords);
        setTotalPages(result.totalPages);
      } else {
        // Es Array - modo legacy (fallback)
        setQuestionaries(result);
        setTotalRecords(result.length);
        setTotalPages(Math.ceil(result.length / itemsPerPage));
      }
    } catch (error) {
      console.error("Failed to load questionaries:", error);
      setNotification({
        show: true,
        variant: "danger",
        title: "Error de Carga",
        message: "No se pudieron cargar los cuestionarios.",
      });
    } finally {
      setLoading(false);
    }
  }, [
    user,
    props.userId,
    showOnlyCompleted,
    currentPage,
    itemsPerPage,
    debouncedSearchText,
  ]);

  // Cargar datos cuando cambian las dependencias
  useEffect(() => {
    if (!isLoading) {
      loadQuestionaries();
    }
  }, [isLoading, loadQuestionaries]);

  // Resetear a página 1 cuando cambia el filtro, toggle o items per page
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, showOnlyCompleted, itemsPerPage]);

  const onSave = async (newQuestionary: Questionary) => {
    try {
      const response = await QuestionaryService.saveQuestionary(newQuestionary);
      if (response.ok) {
        setNotification({
          show: true,
          variant: "success",
          title: "Éxito",
          message: "Guardado correctamente.",
        });
        await loadQuestionaries();
      } else {
        throw new Error("Server error");
      }
    } catch (e) {
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: "No se pudo guardar.",
      });
    } finally {
      setModalShow(false);
    }
  };

  const onDelete = async (id: number) => {
    try {
      const res = await QuestionaryService.deleteQuestionary(id);
      if (res.ok) {
        setNotification({
          show: true,
          variant: "success",
          title: "Éxito",
          message: "Eliminado correctamente.",
        });
        await loadQuestionaries();
      } else throw new Error("Server error");
    } catch (e) {
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: "No se pudo eliminar.",
      });
    }
  };

  const handleCopyLink = (id: number) => {
    navigator.clipboard
      .writeText(`${window.location.origin}/externalquestionary/${id}`)
      .then(() => {
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 3000);
      });
  };

  const setQuestionaryIdForNavigation = (id: number) => {
    localStorage.setItem("questionaryId", id.toString());
  };

  const FormattedDate = ({
    isoDateString,
  }: {
    isoDateString: string | undefined;
  }) => {
    if (isoDateString === undefined) return <span>N/A</span>;
    const date = new Date(isoDateString);

    const options = {
      year: "numeric",
      month: "2-digit" as "2-digit",
      day: "2-digit" as "2-digit",
    } as const;

    const formattedDate = new Intl.DateTimeFormat("es-ES", options).format(
      date,
    );

    return <span>{formattedDate}</span>;
  };

  const handleArchived = async (questionary: Questionary) => {
    const response = await QuestionaryService.archived(questionary);

    if (response.ok) {
      setNotification({
        show: true,
        variant: "success",
        title: "Éxito",
        message: questionary.isArchived
          ? "Cuestionario restaurado correctamente."
          : "Cuestionario archivado correctamente.",
      });
      await loadQuestionaries();
      return;
    } else {
      const errorMessage = await response.text();
      setNotification({
        show: true,
        variant: "danger",
        title: "Error",
        message: errorMessage || "Error al archivar cuestionario.",
      });
    }
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Generar items de paginación
  const renderPaginationItems = () => {
    if (totalPages === 0) return null;

    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    return items;
  };

  // Helper para obtener el nombre correcto (compatible con DTO y entidad)
  const getUserName = (q: any) => {
    return q.userName || q.user?.name || "";
  };

  const getQuestionaryTypeName = (q: any) => {
    return q.questionaryTypeName || q.questionaryType?.name || "";
  };

  // Calcular rango de registros mostrados
  const startRecord =
    totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <>
      <div className="notification-container">
        {notification && (
          <Alert
            variant={notification.variant}
            onClose={() => setNotification(null)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{notification.title}</Alert.Heading>
            {notification.message}
          </Alert>
        )}
      </div>

      {showCopiedMessage && (
        <div className="popup-message">Enlace copiado.</div>
      )}

      <ModalNewQuestionary
        show={modalShow}
        onHide={() => setModalShow(false)}
        onSave={onSave}
        userId={props.userId}
      />

      <Container className="pt-3">
        <Card>
          <Card.Body>
            <Card.Title>Cuestionarios</Card.Title>
            <hr />

            <Row className="mb-3 g-3 align-items-center">
              <Col md={5}>
                <Form.Control
                  type="text"
                  placeholder="Buscar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Col>

              {user?.role === "Administrador" && (
                <Col
                  md={2}
                  className="d-flex justify-content-md-start align-items-center"
                >
                  <Button
                    onClick={() => setShowArchivedModal(true)}
                    variant="primary"
                    className="d-flex align-items-center"
                  >
                    <FontAwesomeIcon icon={faArchive} className="white" />
                    <span className="ms-2">Archivados</span>
                  </Button>
                </Col>
              )}

              <Col
                md={user?.role === "Administrador" ? 5 : 7}
                className="d-flex justify-content-md-end align-items-center"
              >
                <Stack direction="horizontal" gap={3}>
                  <Form.Check
                    type="switch"
                    id="completeSwitch"
                    label="Solo Completados"
                    checked={showOnlyCompleted}
                    onChange={(e) => setShowOnlyCompleted(e.target.checked)}
                  />
                  {(user?.role === "Administrador" ||
                    user?.role === "Auditor") && (
                    <Button
                      variant="primary"
                      onClick={() => setModalShow(true)}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faPlus} className="white" />
                      <span className="ms-2">Nuevo Cuestionario</span>
                    </Button>
                  )}
                </Stack>
              </Col>
            </Row>

            {/* Indicador de carga */}
            {loading && (
              <div className="text-center my-3">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </Spinner>
              </div>
            )}

            {/* Info de registros y selector de items por página */}
            {!loading && totalRecords > 0 && (
              <Row className="mb-2 align-items-center">
                <Col md={6}>
                  <div className="text-muted small">
                    Mostrando {startRecord} a {endRecord} de {totalRecords}{" "}
                    registros
                  </div>
                </Col>
                <Col md={6} className="text-end">
                  <Form.Group
                    as={Row}
                    className="align-items-center justify-content-end"
                  >
                    <Form.Label
                      column
                      sm="auto"
                      className="small text-muted me-2"
                    >
                      Mostrar:
                    </Form.Label>
                    <Col sm="auto">
                      <Form.Select
                        size="sm"
                        value={itemsPerPage}
                        onChange={(e) =>
                          setItemsPerPage(Number(e.target.value))
                        }
                        style={{ width: "auto" }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Form.Select>
                    </Col>
                    <Form.Label
                      column
                      sm="auto"
                      className="small text-muted ms-2"
                    >
                      por página
                    </Form.Label>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Table striped bordered hover responsive>
              <thead>
                <tr className="text-center">
                  <th>Id</th>
                  <th>Nombre</th>
                  <th>E-mail</th>
                  <th>Tipo</th>
                  <th>Fecha creación</th>
                  <th>Creador</th>
                  <th>Completado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {questionaries.map((q) => (
                  <tr key={q.id}>
                    <td className="text-center">{q.id}</td>
                    <td>{q.name}</td>
                    <td>{q.email}</td>
                    <td>{getQuestionaryTypeName(q)}</td>
                    <td className="text-center">
                      <FormattedDate
                        isoDateString={q.creationDate?.toString()}
                      />
                    </td>
                    <td>{getUserName(q)}</td>
                    <td className="text-center">
                      <span
                        className={`badge rounded-pill ${
                          q.statusId === 3
                            ? "bg-success"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {q.statusId === 3 ? "Sí" : "No"}
                      </span>
                    </td>
                    <td className="text-center">
                      <Stack
                        direction="horizontal"
                        gap={2}
                        className="justify-content-center"
                      >
                        <Button
                          variant="primary"
                          size="sm"
                          active
                          title="Seleccionar/Llenar"
                          onClick={() => setQuestionaryIdForNavigation(q.id!)}
                          href="/FillQuestionary"
                        >
                          <FontAwesomeIcon icon={faEdit} className="white" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          active
                          title="Copiar Link"
                          onClick={() => handleCopyLink(q.id!)}
                        >
                          <FontAwesomeIcon icon={faLink} className="white" />
                        </Button>
                        {user?.role === "Administrador" && (
                          <>
                            <ArchiveButton
                              title="Archivar"
                              description="¿Estás seguro que deseas archivar el registro?"
                              iconButton={faBoxArchive}
                              onClick={() => handleArchived(q)}
                            />
                            <DeleteButton onClick={() => onDelete(q.id!)} />
                          </>
                        )}
                      </Stack>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  />
                  {renderPaginationItems()}
                  <Pagination.Next
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}

            {questionaries.length === 0 && !loading && (
              <div className="text-center text-muted mt-3">
                No se encontraron cuestionarios.
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/** Modal de Archivados */}
      <ModalArchivedQuestionaries
        onHide={() => setShowArchivedModal(false)}
        show={showArchivedModal}
        onRestore={handleArchived}
      />
    </>
  );
};
